/**
 * Self-issued email-only signer session (#2955, Phase 3/5).
 *
 * Email-only (magic-link) signers no longer authenticate through Cognito
 * CUSTOM_AUTH. Instead `LoginByToken` exchanges the magic-link token for a
 * self-issued RS256 JWT (`POST /api/auth/session`), stored HERE in
 * `sessionStorage` and attached as a `Bearer` token on API calls. The in-account
 * Phase-2 authorizer verifies it. Admins and SSO signers are unaffected — they
 * never use magic-link login and keep their Cognito/Amplify session.
 *
 * `sessionStorage` (not `localStorage`) matches the default non-"remember me"
 * behavior of the Cognito path: the session is dropped when the tab closes.
 */

const SIGNER_SESSION_KEY = 'accedo.signerSession';

/** The minimal claim shape the SPA reads from a decoded signer JWT. */
export interface SignerSessionClaims {
    userId: string;
    tenantId: string;
    email: string | null;
    userType: 'signer';
    exp: number;
}

/**
 * Persist a freshly-minted signer JWT to sessionStorage. Storage access can
 * throw in privacy modes; callers should treat a throw as a failed sign-in.
 */
export function storeSignerSession(token: string): void {
    window.sessionStorage.setItem(SIGNER_SESSION_KEY, token);
}

/**
 * Return the raw signer JWT if a NON-EXPIRED session is present, else `null`.
 * An expired token is cleared as a side effect so a stale value never lingers.
 */
export function getSignerSessionToken(): string | null {
    let token: string | null;
    try {
        token = window.sessionStorage.getItem(SIGNER_SESSION_KEY);
    } catch {
        return null;
    }
    if (!token) {
        return null;
    }
    const claims = decodeSignerClaims(token);
    if (!claims || isExpired(claims)) {
        clearSignerSession();
        return null;
    }
    return token;
}

/** Decode and return the signer claims if a valid, non-expired session exists. */
export function getSignerSessionClaims(): SignerSessionClaims | null {
    const token = getSignerSessionToken();
    if (!token) {
        return null;
    }
    return decodeSignerClaims(token);
}

/** True when a valid (non-expired) signer session is present. */
export function hasSignerSession(): boolean {
    return getSignerSessionToken() !== null;
}

/** Remove the signer session (sign-out / session-expiry cleanup). */
export function clearSignerSession(): void {
    try {
        window.sessionStorage.removeItem(SIGNER_SESSION_KEY);
    } catch {
        /* private mode — nothing to clear */
    }
}

/** base64url → utf8 JSON-decode of a JWT payload segment. */
function decodeSignerClaims(token: string): SignerSessionClaims | null {
    const parts = token.split('.');
    if (parts.length !== 3) {
        return null;
    }
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const payload = JSON.parse(json) as Record<string, unknown>;
        if (typeof payload.userId !== 'string' || typeof payload.tenantId !== 'string' || typeof payload.exp !== 'number') {
            return null;
        }
        return {
            userId: payload.userId,
            tenantId: payload.tenantId,
            email: typeof payload.email === 'string' ? payload.email : null,
            userType: 'signer',
            exp: payload.exp,
        };
    } catch {
        return null;
    }
}

/** True when the token's `exp` (seconds) is at or before now. */
function isExpired(claims: SignerSessionClaims): boolean {
    return claims.exp * 1000 <= Date.now();
}
